// The run-complete screen.
//
// Split in two on purpose. `resultsView()` is a pure function from a finished run to the strings on
// screen, testable in node like the engine; `ResultsScreen` is a thin DOM binding that renders
// those strings and owns the clipboard. Everything worth getting wrong lives in the pure half.
//
// This is where the score stops being a mystery. Stress is worth up to 20 points and is completely
// invisible while playing — a player who finishes on 71 with no idea why will read the game as
// arbitrary, which is the one impression a daily cannot survive. So the penalty breakdown is shown
// here rather than buried in a help screen.

import { calmBar, formatDuration, shareText } from '../engine/share'
import { displayStreak } from '../engine/stats'
import type { Stats } from '../engine/stats'
import type { ScoreBreakdown } from '../engine/score'
import type { Level, RunResult } from '../engine/types'

export interface Readout {
  label: string
  value: string
}

export interface ResultsView {
  headline: string
  /** The score alone. The `/100` is static chrome and lives in the markup. */
  score: string
  detail: string
  calm: string
  /** Empty for an unfinished run, whose score does not come from these penalties at all. */
  penalties: Readout[]
  /** What went wrong, in words, or empty when nothing did. */
  faults: string
  stats: Readout[]
  /** The share card verbatim, so the copy button and the screen cannot drift apart. */
  share: string
}

export interface ResultsInput {
  day: number
  /** The Sydney date key of the run, for staleness in the displayed streak. */
  dayKey: string
  level: Level
  result: RunResult
  score: ScoreBreakdown
  stats: Stats
}

/**
 * Round a set of penalties to integers that still sum to the points actually lost.
 *
 * Rounding each one independently is the obvious approach and it produces a screen that fails
 * arithmetic: penalties of 2.4, 0 and 3.4 display as 2, 0 and 3 against a score of 94, and the
 * player reads 100 − 5 = 95 and concludes the number is made up. Largest-remainder apportionment
 * spends the rounding difference on whichever penalty has the largest fraction, so the column adds
 * up exactly. Ties break by index, so this stays deterministic.
 *
 * When the score has been clamped, the raw penalties genuinely do not sum to the points lost, and
 * inventing a difference to hide that would be a lie rather than a rounding. Round plainly instead.
 */
export function apportionPenalties(raw: number[], lost: number): number[] {
  const floors = raw.map((v) => Math.floor(v))
  const spare = lost - floors.reduce((a, b) => a + b, 0)
  if (spare < 0 || spare > raw.length) return raw.map((v) => Math.round(v))

  const order = raw
    .map((v, i) => ({ i, frac: v - Math.floor(v) }))
    .sort((a, b) => b.frac - a.frac || a.i - b.i)

  const out = floors.slice()
  for (let k = 0; k < spare; k++) out[order[k].i] += 1
  return out
}

const signed = (n: number): string => (n > 0 ? `−${n}` : '0')

/**
 * The faults in words, for the line under the breakdown.
 *
 * Counts, not points — the points are in the column above. "Rushed them 3 times" is the sentence
 * that makes "−8" mean something, which is the whole reason M10 exists: a player who cannot see why
 * they scored 71 concludes the game is arbitrary.
 */
export function faultLine(result: RunResult): string {
  const f = result.faults
  const parts: string[] = []
  const add = (n: number, one: string, many: string) => {
    if (n > 0) parts.push(n === 1 ? one : `${many} ×${n}`)
  }
  add(f.rushes, 'rushed them once', 'rushed them')
  add(f.bites, 'bit the stock', 'bit the stock')
  add(f.crosses, 'crossed once', 'crossed')
  add(f.penEscapes, 'lost one from the pen', 'lost them from the pen')
  add(result.barks, 'barked once', 'barked')
  return parts.join(' · ')
}

export function resultsView({ day, dayKey, level, result, score, stats }: ResultsInput): ResultsView {
  const detail = result.won
    ? `${formatDuration(score.durationMs)} · par ${formatDuration(level.parSeconds * 1000)} · ${
        result.flockSize
      } sheep · ${result.barks} bark${result.barks === 1 ? '' : 's'}`
    : `penned ${result.penned}/${result.flockSize} · ${formatDuration(score.durationMs)}`

  // Only a finished run's score is 100 minus these. An unfinished one is ranked on a separate 0..19
  // scale (see score.ts), so showing a breakdown that does not explain its number would mislead.
  const penalties: Readout[] = []
  if (result.won) {
    // Every line the run actually lost points on, apportioned so the column adds up exactly. Faults
    // the player did not commit are left off rather than shown as zeroes — a scorecard of mostly
    // noughts buries the two lines that matter.
    const rows: { label: string; raw: number }[] = [
      { label: 'TIME', raw: score.timePenalty },
      { label: 'BARKS', raw: score.barkPenalty },
      { label: 'RUSHED', raw: score.stressPenalty },
      { label: 'BIT', raw: score.bitePenalty },
      { label: 'ESCAPED', raw: score.escapePenalty },
      { label: 'CROSSED', raw: score.crossPenalty },
    ].filter((r) => r.raw > 0)

    const apportioned = apportionPenalties(
      rows.map((r) => r.raw),
      100 - score.primary,
    )
    rows.forEach((r, i) => penalties.push({ label: r.label, value: signed(apportioned[i]) }))
  }

  return {
    headline: result.won ? 'FLOCK PENNED' : 'DID NOT FINISH',
    score: String(score.primary),
    detail,
    calm: `${calmBar(result.meanStress)} calm`,
    faults: faultLine(result),
    penalties,
    stats: [
      { label: 'STREAK', value: String(displayStreak(stats, dayKey)) },
      { label: 'PLAYED', value: String(stats.played) },
      { label: 'BEST', value: String(stats.bestScore) },
    ],
    share: shareText({ day, level, result, score }),
  }
}

/** How long the copy button stays acknowledged before reverting. */
const COPIED_MS = 1400

/** Shared by the results panel and the landing screen, so the two read as the same instrument. */
export function readoutsHtml(list: Readout[]): string {
  return list
    .map(
      (r) =>
        `<div class="readout"><dt>${r.label}</dt><dd class="mono">${r.value}</dd></div>`,
    )
    .join('')
}

export class ResultsScreen {
  private readonly headline: HTMLElement
  private readonly score: HTMLElement
  private readonly detail: HTMLElement
  private readonly calm: HTMLElement
  private readonly faults: HTMLElement
  private readonly penalties: HTMLElement
  private readonly stats: HTMLElement
  private readonly copyButton: HTMLButtonElement
  private readonly fallback: HTMLTextAreaElement
  private share = ''
  private revertTimer = 0

  constructor(root: HTMLElement) {
    const pick = <T extends HTMLElement>(id: string): T => {
      const el = root.querySelector<T>(`#${id}`)
      if (!el) throw new Error(`results screen is missing #${id}`)
      return el
    }
    this.headline = pick('results-headline')
    this.score = pick('results-score')
    this.detail = pick('results-detail')
    this.calm = pick('results-calm')
    this.faults = pick('results-faults')
    this.penalties = pick('results-penalties')
    this.stats = pick('results-stats')
    this.copyButton = pick<HTMLButtonElement>('results-copy')
    this.fallback = pick<HTMLTextAreaElement>('results-fallback')

    this.copyButton.addEventListener('click', () => void this.copy())
  }

  /**
   * Populate the panel. Visibility belongs to `Screens`, not here — two owners of `hidden` is how a
   * screen ends up showing when it should not.
   */
  render(view: ResultsView): void {
    this.headline.textContent = view.headline
    this.score.textContent = view.score
    this.detail.textContent = view.detail
    this.calm.textContent = view.calm
    this.faults.textContent = view.faults
    this.faults.hidden = view.faults === ''
    this.penalties.innerHTML = readoutsHtml(view.penalties)
    this.penalties.hidden = view.penalties.length === 0
    this.stats.innerHTML = readoutsHtml(view.stats)
    this.share = view.share
    this.fallback.value = view.share
    this.fallback.hidden = true
  }

  private async copy(): Promise<void> {
    if (await writeClipboard(this.share)) {
      this.acknowledge('COPIED')
      return
    }
    // Clipboard access is refused in insecure contexts and by some mobile browsers without a very
    // recent user gesture. Rather than fail silently, hand the player the text to copy by hand.
    this.fallback.hidden = false
    this.fallback.select()
    this.acknowledge('SELECT AND COPY')
  }

  private acknowledge(label: string): void {
    this.copyButton.textContent = label
    clearTimeout(this.revertTimer)
    this.revertTimer = setTimeout(() => {
      this.copyButton.textContent = 'COPY RESULT'
    }, COPIED_MS) as unknown as number
  }
}

async function writeClipboard(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text)
    return true
  } catch {
    return false
  }
}
